// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - DAILY STREAKS MANAGER
// Track consecutive days of purification with bonus rewards
// Supabase = source of truth; localStorage = fast cache
// ═══════════════════════════════════════════════════════════════════════════

import { saveStreakToSupabase, getStreakFromSupabase } from './supabaseManager';

const STREAK_STORAGE_KEY = 'distil_daily_streak';

// Streak bonus milestones
export const STREAK_MILESTONES = {
  3: { bonus: 50, name: 'Hot Streak' },
  7: { bonus: 100, name: 'Week Warrior' },
  14: { bonus: 250, name: 'Fortnight Fighter' },
  30: { bonus: 500, name: 'Monthly Master' },
  60: { bonus: 1000, name: 'Dedication Legend' },
  100: { bonus: 2000, name: 'Century Champion' },
};

// ── Wallet-scoped storage key ──────────────────────────────────────────────
function getStorageKey(walletAddress) {
  if (!walletAddress) return `${STREAK_STORAGE_KEY}_demo`;
  return `${STREAK_STORAGE_KEY}_${walletAddress}`;
}

/**
 * Get current streak data from localStorage
 */
export function getStreakData(walletAddress = null) {
  try {
    const key = getStorageKey(walletAddress);
    const stored = localStorage.getItem(key);
    if (!stored) {
      return {
        currentStreak: 0,
        longestStreak: 0,
        lastPurificationDate: null,
        totalPurificationDays: 0,
        nextMilestone: 3,
        nextMilestoneBonus: 50,
      };
    }
    const data = JSON.parse(stored);
    const nextMilestone = Object.keys(STREAK_MILESTONES)
      .map(Number)
      .find(m => m > data.currentStreak) || null;
    return {
      ...data,
      nextMilestone,
      nextMilestoneBonus: nextMilestone ? STREAK_MILESTONES[nextMilestone].bonus : null,
    };
  } catch (error) {
    console.error('Error getting streak data:', error);
    return { currentStreak: 0, longestStreak: 0, lastPurificationDate: null, totalPurificationDays: 0, nextMilestone: 3, nextMilestoneBonus: 50 };
  }
}

/**
 * Save streak data to localStorage
 */
function saveStreakData(data, walletAddress = null) {
  try {
    const key = getStorageKey(walletAddress);
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving streak data:', error);
  }
}

/**
 * Restore streak from Supabase into localStorage (call on wallet connect)
 * Returns the restored streak data or null if nothing to restore
 */
export async function syncStreakFromSupabase(walletAddress) {
  if (!walletAddress) return null;
  try {
    const remote = await getStreakFromSupabase(walletAddress);
    if (!remote) return null;

    const local = getStreakData(walletAddress);

    // Supabase wins if it has a longer streak or more recent date
    const remoteDate = remote.lastPurificationDate ? new Date(remote.lastPurificationDate) : null;
    const localDate = local.lastPurificationDate ? new Date(local.lastPurificationDate) : null;
    const supabaseWins = !localDate || (remoteDate && remoteDate >= localDate) || remote.longestStreak > local.longestStreak;

    if (supabaseWins) {
      const merged = {
        currentStreak: remote.currentStreak,
        longestStreak: Math.max(remote.longestStreak, local.longestStreak),
        lastPurificationDate: remote.lastPurificationDate,
        totalPurificationDays: local.totalPurificationDays || remote.currentStreak,
      };
      saveStreakData(merged, walletAddress);
      console.log('✅ Streak restored from Supabase:', merged.currentStreak, 'days');
      return merged;
    }
    return local;
  } catch (err) {
    console.error('syncStreakFromSupabase failed:', err);
    return null;
  }
}

/**
 * Check if user has purified today
 */
export function hasPurifiedToday(walletAddress = null) {
  const data = getStreakData(walletAddress);
  if (!data.lastPurificationDate) return false;
  const lastDate = new Date(data.lastPurificationDate);
  const today = new Date();
  return (
    lastDate.getFullYear() === today.getFullYear() &&
    lastDate.getMonth() === today.getMonth() &&
    lastDate.getDate() === today.getDate()
  );
}

function daysBetween(date1, date2) {
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.floor((date2 - date1) / oneDay);
}

/**
 * Update streak after purification — saves to localStorage + Supabase
 */
export async function updateStreak(walletAddress = null) {
  const data = getStreakData(walletAddress);
  const now = new Date();
  const today = now.toISOString();

  if (hasPurifiedToday(walletAddress)) {
    return { ...data, bonusAwarded: 0, milestoneReached: null, streakBroken: false };
  }

  let newStreak = 1;
  let streakBroken = false;

  if (data.lastPurificationDate) {
    const lastDate = new Date(data.lastPurificationDate);
    const daysSince = daysBetween(lastDate, now);
    if (daysSince === 0) {
      newStreak = data.currentStreak;
    } else if (daysSince === 1) {
      newStreak = data.currentStreak + 1;
    } else {
      newStreak = 1;
      streakBroken = data.currentStreak > 0;
    }
  }

  const milestoneReached = STREAK_MILESTONES[newStreak] || null;
  const bonusAwarded = milestoneReached ? milestoneReached.bonus : 0;

  const updatedData = {
    currentStreak: newStreak,
    longestStreak: Math.max(newStreak, data.longestStreak || 0),
    lastPurificationDate: today,
    totalPurificationDays: (data.totalPurificationDays || 0) + 1,
  };

  // Save to localStorage immediately (fast)
  saveStreakData(updatedData, walletAddress);

  // Save to Supabase in background (non-blocking)
  if (walletAddress) {
    saveStreakToSupabase(walletAddress, updatedData).catch(err =>
      console.error('Streak Supabase sync failed (non-blocking):', err)
    );
  }

  const nextMilestone = Object.keys(STREAK_MILESTONES).map(Number).find(m => m > newStreak) || null;

  return {
    ...updatedData,
    bonusAwarded,
    milestoneReached,
    streakBroken,
    nextMilestone,
    nextMilestoneBonus: nextMilestone ? STREAK_MILESTONES[nextMilestone].bonus : null,
  };
}

/**
 * Reset streak (demo/testing only)
 */
export function resetStreak(walletAddress = null) {
  try {
    localStorage.removeItem(getStorageKey(walletAddress));
  } catch (error) {
    console.error('Error resetting streak:', error);
  }
}

export function getStreakMessage(streak) {
  if (streak === 0) return "Start your cleanup journey!";
  if (streak === 1) return "Great start! Come back tomorrow!";
  if (streak < 3) return `${streak} days! Keep it up!`;
  if (streak < 7) return `${streak} day streak! You're on fire! 🔥`;
  if (streak < 14) return `${streak} days strong! Incredible dedication! ⭐`;
  if (streak < 30) return `${streak} day streak! You're a legend! 👑`;
  if (streak < 60) return `${streak} days! Unbelievable commitment! 💎`;
  return `${streak} day streak! You're unstoppable! 🚀`;
}

export function getTimeUntilStreakBreak(lastPurificationDate) {
  if (!lastPurificationDate) return { hours: 24, minutes: 0, critical: false };
  const lastDate = new Date(lastPurificationDate);
  const now = new Date();
  const tomorrow = new Date(lastDate);
  tomorrow.setDate(tomorrow.getDate() + 2);
  tomorrow.setHours(23, 59, 59, 999);
  const msRemaining = tomorrow - now;
  if (msRemaining <= 0) return { hours: 0, minutes: 0, critical: true, expired: true };
  const hoursRemaining = Math.floor(msRemaining / (1000 * 60 * 60));
  const minutesRemaining = Math.floor((msRemaining % (1000 * 60 * 60)) / (1000 * 60));
  return { hours: hoursRemaining, minutes: minutesRemaining, critical: hoursRemaining < 6, expired: false };
}

export default {
  getStreakData,
  hasPurifiedToday,
  updateStreak,
  syncStreakFromSupabase,
  resetStreak,
  getStreakMessage,
  getTimeUntilStreakBreak,
  STREAK_MILESTONES,
};
